import { AssetReference } from '@domain/creativeset/element-asset';
import { WIDGET_PROPERTY_PREFIX } from '@domain/widget';
import { getWidgetContentUrlOfElement } from '@studio/utils/element.utils';
import { PromiseResolver } from '@studio/utils/promises';
import { resolveNamespacedCharacters } from '@studio/utils/utils';
import { isWidgetElement, isWidgetNode } from '../../nodes/helpers';
import { deserializeWidgetPropertyValue } from '../../serialization/widget-serializer';
export const codePropertyRegex = /^(html|css|ts|js)$/;
export const designWidgetElementCodePropertyRegex = /^(html|css|js)$/;
export function stripCustomPropertyPrefix(propertyName) {
    return propertyName.replace(WIDGET_PROPERTY_PREFIX, '');
}
export function applyCustomPropertyPrefix(propertyName) {
    if (propertyName.indexOf(WIDGET_PROPERTY_PREFIX) > -1) {
        return propertyName;
    }
    return `${WIDGET_PROPERTY_PREFIX}${propertyName}`;
}
export function isWidgetReference(property) {
    return property.name === AssetReference.Widget;
}
export function isWidgetContentBlobReference(property) {
    return property.name === AssetReference.WidgetContentUrl;
}
export function isBFLibraryWidgetReference(property) {
    return property.name === AssetReference.BannerflowLibraryWidget;
}
export function isAnyWidgetReference(property) {
    return (isWidgetReference(property) ||
        isWidgetContentBlobReference(property) ||
        isBFLibraryWidgetReference(property));
}
export function isCustomProperty(property) {
    return property.name.indexOf(WIDGET_PROPERTY_PREFIX) > -1;
}
export function isWidgetCodeProperty(property) {
    return property.name !== undefined && property.name.match(codePropertyRegex) !== null;
}
export function getIframeIdFromEvent(mouseEvent) {
    var _a, _b, _c, _d, _e;
    const currentTarget = mouseEvent.currentTarget;
    if (!currentTarget) {
        return;
    }
    if (currentTarget.defaultView) {
        return (_b = (_a = currentTarget.defaultView) === null || _a === void 0 ? void 0 : _a.frameElement) === null || _b === void 0 ? void 0 : _b.id;
    }
    return (_e = (_d = (_c = currentTarget === null || currentTarget === void 0 ? void 0 : currentTarget.ownerDocument) === null || _c === void 0 ? void 0 : _c.defaultView) === null || _d === void 0 ? void 0 : _d.frameElement) === null || _e === void 0 ? void 0 : _e.id;
}
export function translateOpenEventPosition(mouseEvent, widgetInstance) {
    if (!(widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.instance)) {
        return {
            x: mouseEvent.clientX,
            y: mouseEvent.clientY
        };
    }
    const { x: widgetX, y: widgetY } = widgetInstance.instance;
    // mouseEvent.clientX and clientY values are relative to the iframe
    return {
        x: mouseEvent.clientX + widgetX,
        y: mouseEvent.clientY + widgetY
    };
}
export function isValidWidgetResourceData(data) {
    return !!data && typeof data === 'object' && 'html' in data && 'css' in data && 'js' in data;
}
const widgetResourceCache = new Map();
export async function fetchWidgetFromResource(resourceUrl) {
    resourceUrl = resourceUrl.replace('http://storage-emulator:10000', 'https://blob-storage.bannerflow.local');
    const existingResource = widgetResourceCache.get(resourceUrl);
    if (existingResource) {
        return await existingResource.promise;
    }
    const loadWidgetResource = new PromiseResolver();
    const promiseObj = { promise: loadWidgetResource.promise };
    let response;
    widgetResourceCache.set(resourceUrl, promiseObj);
    try {
        response = await (await fetch(resourceUrl)).json();
    }
    catch (_a) {
        loadWidgetResource.reject();
        throw new Error('Could not fetch widget resource.');
    }
    if (!isValidWidgetResourceData(response)) {
        throw new Error('Invalid widget resource data.');
    }
    widgetResourceCache.set(resourceUrl, promiseObj);
    loadWidgetResource.resolve(response);
    return response;
}
export const DEFAULT_WIDGET_JS = `Widget.on(WidgetEvent.PropertyChanged, () => {
    console.log(Widget.properties);
})`;
export const DEFAULT_WIDGET_HTML = `<div>Hello!</div>\n`;
export const DEFAULT_WIDGET_CSS = `html, body {
    padding: 0;
    margin: 0;
}`;
export async function getWidgetCodeOfElement(element) {
    const resourceUrl = getWidgetContentUrlOfElement(element);
    if (!resourceUrl) {
        throw new Error('No widget content blob url found.');
    }
    return await fetchWidgetFromResource(resourceUrl);
}
export async function applyWidgetCodeOnWidgetNodes(design) {
    for (const element of design.elements) {
        if (isWidgetElement(element)) {
            const node = design.document.findNodeById_m(element.id);
            if (isWidgetNode(node)) {
                const { html, css, js } = await getWidgetCodeOfElement(element);
                node.html = html;
                node.css = css;
                node.js = js;
            }
        }
    }
}
export function mapElementToCustomWidgetProperties(element) {
    return element.properties
        .filter(isCustomProperty)
        .map((property) => {
        const value = deserializeWidgetPropertyValue(property);
        return {
            name: stripCustomPropertyPrefix(property.name),
            label: resolveNamespacedCharacters(property.label),
            unit: property.unit,
            value: value,
            versionPropertyId: property.versionPropertyId
        };
    });
}
export function isWidgetImage(value) {
    return (typeof value === 'object' &&
        'id' in value &&
        'src' in value &&
        typeof value.id === 'string' &&
        typeof value.src === 'string');
}
export function isWidgetFont(value) {
    return (typeof value === 'object' &&
        'id' in value &&
        'src' in value &&
        'style' in value &&
        typeof value.id === 'string' &&
        typeof value.src === 'string');
}
